import { trackEventPass, trackPassesInCart } from '@/utils/eventPassTracking';
import React, { useState, useEffect } from 'react';
import { Trash2, ShoppingBag, Loader2 } from 'lucide-react';
import { CartPassLine, CartTotals, chf } from '@/components/cart/CartPassLine';
import { Button } from '@/components/ui/button';
import { getCart, removeFromCart, clearCart, PRICES, addToCart, saveCart, calculateTotal } from '@/utils/cart';
import { ShoppingCart, CartItem } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import posthogLib from 'posthog-js';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { CollectionFanPreview } from '@/components/ui/collection-fan-preview';

interface CartContentProps {
  showClearButton?: boolean;
  showHeader?: boolean;
  showCheckout?: boolean;
  onClearCart?: () => void;
  emptyCartContent?: React.ReactNode;
}

export const CartContent: React.FC<CartContentProps> = ({ 
  showClearButton = true,
  showHeader = false,
  showCheckout = true,
  onClearCart,
  emptyCartContent
}) => {
  const { t } = useTranslation();
  const posthog = posthogLib.__loaded ? posthogLib : null;
  const [cart, setCart] = useState<ShoppingCart>({ items: [], total: 0 });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<{ url: string; alt: string } | null>(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

  useEffect(() => {
    const currentCart = getCart();
    // Detect truly malformed legacy items (missing event/race metadata).
    // Note: empty swissEquestrianRiderId is tolerated — the cart/checkout
    // identifies items by participantId + photoId.
    const hasOldItems = currentCart.items.some(item =>
      !item.eventId || !item.raceId
    );

    if (hasOldItems) {
      console.log('Found old cart items without metadata, clearing cart...');
      handleClearCart();
    } else {
      setCart(currentCart);
    }
  }, []);

  useEffect(() => {
    const updateCartData = () => {
      setCart(getCart());
    };

    window.addEventListener('cartUpdated', updateCartData);
    return () => window.removeEventListener('cartUpdated', updateCartData);
  }, []);

  const handleViewPhoto = (item: CartItem) => {
    if (item.type === 'single' && item.photoId && item.eventId && item.raceId) {
      // For winner photos, use different URL structure
      const imageUrl = item.raceId === 'winners' 
        ? `https://api.equiphoto.ch/thumbnails/${item.eventId}/winners/${item.photoId}`
        : `https://api.equiphoto.ch/thumbnails/${item.eventId}/${item.raceId}/${item.photoId}`;
      setLightboxImage({
        url: imageUrl,
        alt: `${item.participantName} - Photo #${item.photoId}`
      });
      setLightboxOpen(true);
    }
  };

  const handleRemoveItem = (participantId: string, type: CartItem['type'], photoId?: number) => {
    if (type === 'event_pass') trackEventPass('removed', { event_id: getCart().items.find(i => i.type === 'event_pass' && i.participantId === participantId)?.eventId, source: 'cart' });
    const before = getCart();
    const updatedCart = removeFromCart(participantId, type, photoId);
    setCart(updatedCart);
    if (type === 'event_pass') {
      toast(t('cart.passRemoved'), { action: { label: t('cart.undo'), onClick: () => { saveCart(before); setCart(before); } } });
    }
  };

  const handleClearCart = () => {
    const emptyCart = clearCart();
    setCart(emptyCart);
    onClearCart?.();
  };


  // Group items by event and race
  const groupedItems = cart.items.reduce((groups, item) => {
    const cat = item.type === 'event_pass' ? 'Event Pass' : item.raceCategory;
    const key = `${item.eventName}-${cat}`;
    if (!groups[key]) {
      groups[key] = {
        eventName: item.eventName,
        raceCategory: cat,
        items: []
      };
    }
    groups[key].items.push(item);
    return groups;
  }, {} as Record<string, { eventName: string; raceCategory: string; items: CartItem[] }>);


  const handleCheckout = async () => {
    trackPassesInCart('checkout_started', cart.items, { source: 'cart' });
    setIsCreatingCheckout(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { cartItems: cart.items, totalAmount: cart.total }
      });
      
      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Failed to create checkout session');
      }
      
      if (data?.error) {
        console.error('Checkout session error:', data.error);
        throw new Error(data.error);
      }
      
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      
      let errorMessage = 'Checkout failed';
      if (error.message.includes('Stripe products not configured')) {
        errorMessage = 'Payment system not configured. Please contact support.';
      } else if (error.message.includes('Stripe settings not found')) {
        errorMessage = 'Payment system not set up. Please contact support.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <>
        {emptyCartContent || (
          <div className="text-center py-12">
            <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">{t('cart.empty')}</h3>
            <p className="text-muted-foreground">{t('cart.addPhotosToStart')}</p>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {showHeader && (
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">{t('cart.title')}</h1>
          {showClearButton && (
            <Button variant="outline" onClick={handleClearCart}>
              {t('cart.clearCart')}
            </Button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(groupedItems).map(([key, group]) => (
          <div key={key} className="overflow-hidden rounded-xl bg-card shadow-sm">
            <div className="bg-surface-container-low px-4 py-3">
              <h3 className="font-semibold [text-wrap:balance]">
                {group.eventName} · {group.raceCategory}
              </h3>
            </div>
            <div className="divide-y-0 px-4 py-2">
              {group.items.map((item) => (
                <div key={`${item.participantId}-${item.type}-${item.photoId || 'collection'}`} className="flex items-center justify-between gap-3 py-3">
                  {item.type === 'event_pass' ? (
                    <CartPassLine item={item} />
                  ) : (
                    <div className="flex min-w-0 flex-1 items-center gap-4">
                      {item.type === 'single' && item.photoId && (
                        <button
                          type="button"
                          className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-muted outline-none transition-opacity duration-150 hover:opacity-80 focus-visible:ring-2 focus-visible:ring-primary"
                          onClick={() => handleViewPhoto(item)}
                          aria-label={t('cart.singlePhoto', { id: item.photoId })}
                        >
                          {item.eventId && item.raceId ? (
                            <img
                              src={item.raceId === 'winners'
                                ? `https://api.equiphoto.ch/thumbnails/${item.eventId}/winners/${item.photoId}`
                                : `https://api.equiphoto.ch/thumbnails/${item.eventId}/${item.raceId}/${item.photoId}`}
                              alt=""
                              className="h-full w-full rounded-md object-cover outline outline-1 -outline-offset-1 outline-black/10"
                              loading="lazy"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : (
                            <span className="text-xs">#{item.photoId}</span>
                          )}
                        </button>
                      )}
                      {item.type === 'collection' && item.eventId && item.raceId && item.participantId && (
                        <CollectionFanPreview eventId={item.eventId} raceId={item.raceId} participantName={item.participantName} participantId={item.participantId} />
                      )}
                      <div className="min-w-0">
                        <h4 className="font-medium">{item.participantName}</h4>
                        <p className="text-sm text-muted-foreground">{item.horseName}</p>
                        <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-sm tabular-nums">
                          <span className="text-muted-foreground">
                            {item.type === 'single' ? t('cart.singlePhoto', { id: item.photoId }) : t('cart.fullCollection')}
                          </span>
                          <span className="font-semibold text-foreground">{chf(item.price)}</span>
                        </p>
                      </div>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 shrink-0 text-muted-foreground hover:text-foreground"
                    aria-label={t('cart.remove')}
                    onClick={() => handleRemoveItem(item.participantId, item.type, item.photoId)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showCheckout && (
        <div className="mx-auto mt-8 max-w-md lg:max-w-lg">
          <div className="space-y-4 rounded-xl bg-card p-6 shadow-sm">
            <CartTotals items={cart.items} total={cart.total} />
            <Button className="w-full enabled:active:scale-[0.96] transition-transform duration-150" size="lg" onClick={handleCheckout} disabled={isCreatingCheckout}>
              {isCreatingCheckout && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isCreatingCheckout ? t('cart.processing') : t('cart.checkout')}
            </Button>
            <p className="text-center text-xs text-muted-foreground [text-wrap:pretty]">
              {t('eventPass.reassurance')} · {t('cart.paymentMethods')}
            </p>
          </div>
        </div>
      )}

    {/* Lightbox */}
    {lightboxImage && (
      <ImageLightbox
        isOpen={lightboxOpen}
        imageUrl={lightboxImage.url}
        alt={lightboxImage.alt}
        onClose={() => {
          setLightboxOpen(false);
          setLightboxImage(null);
        }}
      />
    )}
  </>
  );
};